/**
 * Canonical platform roles — keep in sync with backend/constants/platformRoles.js
 * and Localized App server/constants/platformRoles.cjs.
 */

export const ACCOUNT_TYPES = [
  'farmer',
  'distributor',
  'processor',
  'individual',
  'admin',
] as const;

export type PlatformAccountType = (typeof ACCOUNT_TYPES)[number];

export const SIGNUP_ACCOUNT_TYPES = ACCOUNT_TYPES.filter(
  (r): r is Exclude<PlatformAccountType, 'admin'> => r !== 'admin'
);

export const ACCOUNT_TYPE_LABELS: Record<PlatformAccountType, string> = {
  farmer: 'Farmer',
  distributor: 'Distributor',
  processor: 'Processor',
  individual: 'Individual',
  admin: 'Platform Admin',
};

export const ACCOUNT_TYPE_HINTS: Record<
  Exclude<PlatformAccountType, 'admin'>,
  string
> = {
  farmer: 'Agricultural production and farm management',
  distributor: 'Distribution and logistics in the supply chain',
  processor: 'Food processing and transformation',
  individual: 'Individual or research collaborator account',
};

export const isPlatformAdmin = (role?: string | null) => role === 'admin';

export const isValidAccountType = (role?: string | null): role is PlatformAccountType =>
  Boolean(role && (ACCOUNT_TYPES as readonly string[]).includes(role));

export const isSignupAccountType = (
  role?: string | null
): role is Exclude<PlatformAccountType, 'admin'> =>
  Boolean(role && (SIGNUP_ACCOUNT_TYPES as readonly string[]).includes(role as PlatformAccountType));

export const normalizeLegacyRole = (role?: string | null): PlatformAccountType => {
  if (!role || role === 'user') return 'individual';
  if (isValidAccountType(role)) return role;
  return 'individual';
};
